import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, signal } from '@angular/core';

@Component({
  selector: 'app-warning-tooltip',
  standalone: true,
  templateUrl: './warning-tooltip.html',
  styleUrl: './warning-tooltip.scss',
})
export class WarningTooltip implements OnChanges {
  @Input() message = '';

  @Input() top = 8;

  @Input() right = 8;

  @Input() announcementRole: 'tooltip' | 'status' = 'tooltip';

  @Input() ariaLive: 'off' | 'polite' = 'off';

  @Input() showInfoIcon = false;

  @Input() appearance: 'warning' | 'suggestion' = 'warning';

  @Input() placement: 'above' | 'below' = 'above';

  @Input() showTooltip = false;

  @ViewChild('infoIcon') private infoIcon?: ElementRef<HTMLButtonElement>;

  protected readonly hoveringInfoIcon = signal(false);

  protected readonly touchTooltip = signal(false);

  protected readonly hoverPosition = signal<{ top: number; right: number } | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['showTooltip']) {
      return;
    }
    if (this.showTooltip && this.infoIcon) {
      this.setTooltipPosition(this.infoIcon.nativeElement);
    } else if (!this.hoveringInfoIcon()) {
      this.hoverPosition.set(null);
    }
  }

  onInfoIconEnter(event: PointerEvent | FocusEvent): void {
    if (event instanceof PointerEvent && event.pointerType === 'touch') {
      this.touchTooltip.set(true);
    }
    this.setTooltipPosition(event.currentTarget as HTMLElement);
    this.hoveringInfoIcon.set(true);
  }

  private setTooltipPosition(icon: HTMLElement): void {
    const rect = icon.getBoundingClientRect();
    this.hoverPosition.set({
      top: this.placement === 'below' ? rect.bottom + 8 : Math.max(8, rect.top - 52),
      right: Math.max(8, window.innerWidth - rect.right - 4),
    });
  }

  onInfoIconClick(): void {
    this.hoveringInfoIcon.set(true);
  }

  onInfoIconLeave(event: PointerEvent): void {
    if (event.pointerType === 'touch' || this.touchTooltip()) {
      return;
    }
    if (document.activeElement === event.currentTarget) {
      return;
    }
    this.hoveringInfoIcon.set(false);
    this.hoverPosition.set(null);
  }

  onInfoIconBlur(): void {
    this.touchTooltip.set(false);
    this.hoveringInfoIcon.set(false);
    this.hoverPosition.set(null);
  }

  get tooltipTop(): number {
    return this.showInfoIcon ? (this.hoverPosition()?.top ?? 8) : this.top;
  }

  get tooltipRight(): number {
    return this.showInfoIcon ? (this.hoverPosition()?.right ?? 8) : this.right;
  }
}
